import React from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useTasks } from '../../hooks/useTasks';
import { useCustomFields } from '../../hooks/useCustomFields';
import { Project, PROJECT_STATUS_OPTIONS, PROJECT_PRIORITY_OPTIONS } from '../../types/project';
import { formatDateTime, formatDate, isOverdue } from '../../utils/dateHelpers';
import Modal from '../common/Modal';
import { 
  CalendarIcon,
  UserIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  CurrencyDollarIcon,
  TagIcon,
  ClockIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  PauseCircleIcon,
  XCircleIcon,
  ClipboardDocumentCheckIcon,
  FireIcon,
  ArrowTrendingUpIcon,
  PlusCircleIcon,
  EyeIcon,
  ListBulletIcon,
  BriefcaseIcon,
  RectangleStackIcon
} from '@heroicons/react/24/outline';

interface ProjectViewProps {
  project: Project;
  isOpen: boolean;
  onClose: () => void;
}

const ProjectView: React.FC<ProjectViewProps> = ({ project, isOpen, onClose }) => {
  const { t } = useLanguage();
  const { tasks } = useTasks();
  const { customFields } = useCustomFields('project');

  
  // Projekthez tartozó taskok szűrése
  const projectTasks = tasks.filter(task => task.projectId === project.id);

  // Státusz szín és ikon lekérése
  const getStatusInfo = (status: Project['status']) => {
    const isDark = document.documentElement.classList.contains('dark');
    
    switch (status) {
      case 'planning':
        return { 
          color: isDark ? '#9ca3af' : '#94a3b8', 
          bgColor: isDark ? '#374151' : '#f1f5f9', 
          icon: ClipboardDocumentCheckIcon, 
          label: PROJECT_STATUS_OPTIONS.find(opt => opt.value === status)?.label || status
        };
      case 'active':
        return { 
          color: isDark ? '#60a5fa' : '#2563eb', 
          bgColor: isDark ? '#1e3a8a' : '#eff6ff', 
          icon: CheckCircleIcon, 
          label: PROJECT_STATUS_OPTIONS.find(opt => opt.value === status)?.label || status
        };
      case 'completed':
        return { 
          color: isDark ? '#34d399' : '#16a34a', 
          bgColor: isDark ? '#065f46' : '#f0fdf4', 
          icon: CheckCircleIcon, 
          label: PROJECT_STATUS_OPTIONS.find(opt => opt.value === status)?.label || status
        };
      case 'paused':
        return { 
          color: isDark ? '#fbbf24' : '#eab308', 
          bgColor: isDark ? '#92400e' : '#fefce8', 
          icon: PauseCircleIcon, 
          label: PROJECT_STATUS_OPTIONS.find(opt => opt.value === status)?.label || status
        };
      case 'cancelled':
        return { 
          color: isDark ? '#f87171' : '#dc2626', 
          bgColor: isDark ? '#7f1d1d' : '#fef2f2', 
          icon: XCircleIcon, 
          label: PROJECT_STATUS_OPTIONS.find(opt => opt.value === status)?.label || status
        };
      default:
        return { 
          color: isDark ? '#9ca3af' : '#94a3b8', 
          bgColor: isDark ? '#374151' : '#f1f5f9', 
          icon: ClipboardDocumentCheckIcon, 
          label: status
        };
    }
  };

  // Prioritás szín és ikon lekérése
  const getPriorityInfo = (priority: Project['priority']) => {
    const isDark = document.documentElement.classList.contains('dark');
    
    switch (priority) {
      case 'low':
        return { 
          color: isDark ? '#34d399' : '#22c55e', 
          bgColor: isDark ? '#065f46' : '#f0fdf4', 
          icon: ArrowTrendingUpIcon, 
          label: PROJECT_PRIORITY_OPTIONS.find(opt => opt.value === priority)?.label || priority
        };
      case 'medium':
        return { 
          color: isDark ? '#fbbf24' : '#eab308', 
          bgColor: isDark ? '#92400e' : '#fefce8', 
          icon: ArrowTrendingUpIcon, 
          label: PROJECT_PRIORITY_OPTIONS.find(opt => opt.value === priority)?.label || priority
        };
      case 'high':
        return { 
          color: isDark ? '#fb923c' : '#f97316', 
          bgColor: isDark ? '#9a3412' : '#fff7ed', 
          icon: ExclamationCircleIcon, 
          label: PROJECT_PRIORITY_OPTIONS.find(opt => opt.value === priority)?.label || priority
        };
      case 'urgent':
        return { 
          color: isDark ? '#f87171' : '#ef4444', 
          bgColor: isDark ? '#7f1d1d' : '#fef2f2', 
          icon: FireIcon, 
          label: PROJECT_PRIORITY_OPTIONS.find(opt => opt.value === priority)?.label || priority
        };
      default:
        return { 
          color: isDark ? '#34d399' : '#22c55e', 
          bgColor: isDark ? '#065f46' : '#f0fdf4', 
          icon: ArrowTrendingUpIcon, 
          label: priority
        };
    }
  };

  // Progressz szín lekérése
  const getProgressColor = (progress: number) => {
    if (progress < 30) return 'bg-red-500';
    if (progress < 70) return 'bg-yellow-500';
    return 'bg-green-500';
  };

  // Határidő ellenőrzés
  const dueDateStatus = () => {
    if (project.status === 'completed') return 'completed';
    if (isOverdue(project.endDate)) return 'overdue';
    
    const today = new Date();
    const endDate = new Date(project.endDate);
    const diffDays = Math.ceil((endDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 3) return 'warning';
    return 'normal';
  };

  const statusInfo = getStatusInfo(project.status);
  const priorityInfo = getPriorityInfo(project.priority);
  const StatusIcon = statusInfo.icon;
  const PriorityIcon = priorityInfo.icon;

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        title={project.name}
        size="5xl"
      >
        <div className="p-4 space-y-4">
        
        {/* 🔵 Alapvető információk */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 rounded-lg p-4 border border-blue-200 dark:border-blue-700">
          <div className="flex items-center mb-3">
            <div className="bg-blue-500 rounded-full p-1.5 mr-2">
              <DocumentTextIcon className="h-4 w-4 text-white" />
            </div>
            <h3 className="text-base font-semibold text-blue-900 dark:text-blue-300">
              {t('projects.sections.basicInfo') || 'Alapvető információk'}
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                {t('projects.fields.name') || 'Projekt neve'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 font-medium text-sm">{project.name}</div>
            </div>

            <div>
              <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                {t('projects.fields.description') || 'Leírás'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 text-sm">{project.description || '-'}</div>
            </div>

            <div>
              <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                {t('projects.fields.status') || 'Státusz'}
              </label>
              <div className="flex items-center">
                <div 
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium mr-2"
                  style={{ backgroundColor: statusInfo.bgColor, color: statusInfo.color }}
                >
                  <StatusIcon className="h-3 w-3 mr-1" />
                  {statusInfo.label}
                </div>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-blue-700 dark:text-blue-300 mb-1">
                {t('projects.fields.priority') || 'Prioritás'}
              </label>
              <div className="flex items-center">
                <div 
                  className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium"
                  style={{ backgroundColor: priorityInfo.bgColor, color: priorityInfo.color }}
                >
                  <PriorityIcon className="h-3 w-3 mr-1" />
                  {priorityInfo.label}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* 🟢 Időzítés és haladás */}
        <div className="bg-gradient-to-r from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 rounded-lg p-4 border border-green-200 dark:border-green-700">
          <div className="flex items-center mb-3">
            <div className="bg-green-500 rounded-full p-1.5 mr-2">
              <ChartBarIcon className="h-4 w-4 text-white" />
            </div>
            <h3 className="text-base font-semibold text-green-900 dark:text-green-300">
              {t('projects.sections.timeProgress') || 'Időzítés és haladás'}
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-xs font-medium text-green-700 dark:text-green-300 mb-1">
                <CalendarIcon className="h-3 w-3 inline mr-1" />
                {t('projects.fields.startDate') || 'Kezdési dátum'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 text-sm">{formatDate(project.startDate)}</div>
            </div>

            <div>
              <label className="block text-xs font-medium text-green-700 dark:text-green-300 mb-1">
                <CalendarIcon className="h-3 w-3 inline mr-1" />
                {t('projects.fields.endDate') || 'Befejezési dátum'}
              </label>
              <div className={`text-gray-900 dark:text-gray-100 text-sm ${dueDateStatus() === 'overdue' ? 'text-red-600 dark:text-red-400 font-medium' : 
                dueDateStatus() === 'warning' ? 'text-yellow-600 dark:text-yellow-400 font-medium' : ''}`}>
                {formatDate(project.endDate)}
                {dueDateStatus() === 'overdue' && (
                  <span className="ml-1 text-red-600 dark:text-red-400 text-xs">
                    ({t('projects.overdue') || 'Lejárt'})
                  </span>
                )}
                {dueDateStatus() === 'warning' && (
                  <span className="ml-1 text-yellow-600 dark:text-yellow-400 text-xs">
                    ({t('projects.dueSoon') || 'Hamarosan lejár'})
                  </span>
                )}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-green-700 dark:text-green-300 mb-1">
                <ChartBarIcon className="h-3 w-3 inline mr-1" />
                {t('projects.fields.progress') || 'Haladás'} ({project.progress}%)
              </label>
              <div className="w-full bg-gray-200 dark:bg-gray-600 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full transition-all duration-300 ${getProgressColor(project.progress)}`}
                  style={{ width: `${project.progress}%` }}
                ></div>
              </div>
            </div>
          </div>
        </div>

        {/* 🟣 Csapat és ügyfél */}
        <div className="bg-gradient-to-r from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
          <div className="flex items-center mb-3">
            <div className="bg-purple-500 rounded-full p-1.5 mr-2">
              <UserIcon className="h-4 w-4 text-white" />
            </div>
            <h3 className="text-base font-semibold text-purple-900 dark:text-purple-300">
              {t('projects.sections.teamClient') || 'Csapat és ügyfél'}
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-purple-700 dark:text-purple-300 mb-1">
                <BuildingOffice2Icon className="h-3 w-3 inline mr-1" />
                {t('projects.fields.customer') || 'Ügyfél'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 text-sm">{project.customerName || '-'}</div>
            </div>

            <div>
              <label className="block text-xs font-medium text-purple-700 dark:text-purple-300 mb-1">
                <UserIcon className="h-3 w-3 inline mr-1" />
                {t('projects.fields.assignedUsers') || 'Hozzárendelt felhasználók'}
              </label>
              <div className="text-gray-900 dark:text-gray-100">
                {project.assignedUsersNames && project.assignedUsersNames.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {project.assignedUsersNames.map((name, index) => (
                      <span 
                        key={index}
                        className="inline-flex items-center px-2 py-0.5 bg-purple-100 dark:bg-purple-900/20 text-purple-800 dark:text-purple-300 text-xs rounded-full"
                      >
                        <UserIcon className="h-2 w-2 mr-1" />
                        {name}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-gray-500 dark:text-gray-400 text-sm">-</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* 🟠 Pénzügyek és címkék */}
        <div className="bg-gradient-to-r from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 rounded-lg p-4 border border-orange-200 dark:border-orange-700">
          <div className="flex items-center mb-3">
            <div className="bg-orange-500 rounded-full p-1.5 mr-2">
              <CurrencyDollarIcon className="h-4 w-4 text-white" />
            </div>
            <h3 className="text-base font-semibold text-orange-900 dark:text-orange-300">
              {t('projects.sections.financeTags') || 'Pénzügyek és címkék'}
            </h3>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-orange-700 dark:text-orange-300 mb-1">
                <CurrencyDollarIcon className="h-3 w-3 inline mr-1" />
                {t('projects.fields.budget') || 'Költségvetés'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 text-sm">
                {project.budget ? `${project.budget.toLocaleString()} Ft` : '-'}
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-orange-700 dark:text-orange-300 mb-1">
                <CurrencyDollarIcon className="h-3 w-3 inline mr-1" />
                {t('projects.fields.actualCost') || 'Tényleges költség'}
              </label>
              <div className="text-gray-900 dark:text-gray-100 text-sm">
                {project.actualCost ? `${project.actualCost.toLocaleString()} Ft` : '-'}
              </div>
            </div>

            <div className="md:col-span-2">
              <label className="block text-xs font-medium text-orange-700 dark:text-orange-300 mb-1">
                <TagIcon className="h-3 w-3 inline mr-1" />
                {t('projects.fields.tags') || 'Címkék'}
              </label>
              <div className="text-gray-900 dark:text-gray-100">
                {project.tags && project.tags.length > 0 ? (
                  <div className="flex flex-wrap gap-1">
                    {project.tags.map((tag, index) => (
                      <span 
                        key={index}
                        className="inline-flex items-center px-2 py-0.5 bg-orange-100 dark:bg-orange-900/20 text-orange-800 dark:text-orange-300 text-xs rounded-full"
                      >
                        <TagIcon className="h-2 w-2 mr-1" />
                        {tag}
                      </span>
                    ))}
                  </div>
                ) : (
                  <span className="text-gray-500 dark:text-gray-400 text-sm">-</span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Jegyzetek */}
        {project.notes && (
          <div className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-900/20 dark:to-gray-800/20 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-center mb-3">
              <div className="bg-gray-500 rounded-full p-1.5 mr-2">
                <DocumentTextIcon className="h-4 w-4 text-white" />
              </div>
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t('projects.fields.notes') || 'Jegyzetek'}
              </h3>
            </div>
            
            <div className="text-gray-900 dark:text-gray-100 text-sm whitespace-pre-wrap">{project.notes}</div>
          </div>
        )}

        {/* Egyedi mezők */}
        {customFields.length > 0 && project.customFields && Object.keys(project.customFields).some(key => {
          const value = project.customFields?.[key];
          return value !== undefined && value !== null && value !== '';
        }) && (
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-900/20 dark:to-indigo-800/20 rounded-lg p-4 border border-purple-200 dark:border-purple-700">
            <div className="flex items-center mb-3">
              <div className="bg-purple-500 rounded-full p-1.5 mr-2">
                <RectangleStackIcon className="h-4 w-4 text-white" />
              </div>
              <h3 className="text-base font-semibold text-purple-900 dark:text-purple-300">
                Egyedi mezők
              </h3>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {customFields.map(field => {
                const value = project.customFields?.[field.key];
                if (value === undefined || value === null || value === '') return null;
                
                return (
                  <div key={field.id}>
                    <label className="block text-xs font-medium text-purple-700 dark:text-purple-300 mb-1">
                      {field.name}
                    </label>
                    <div className="text-gray-900 dark:text-gray-100 text-sm">
                      {Array.isArray(value) ? (
                        <div className="flex flex-wrap gap-1">
                          {value.map((val: string, idx: number) => (
                            <span key={idx} className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300">
                              {val}
                            </span>
                          ))}
                        </div>
                      ) : field.type === 'checkbox' ? (
                        <span>{value ? '✓ Igen' : '✗ Nem'}</span>
                      ) : field.type === 'date' ? (
                        <span>{new Date(value).toLocaleDateString('hu-HU')}</span>
                      ) : (
                        <span>{String(value)}</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Dokumentumok */}
        {project.documents && project.documents.length > 0 && (
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-800/20 rounded-lg p-4 border border-indigo-200 dark:border-indigo-700">
            <div className="flex items-center mb-3">
              <div className="bg-indigo-500 rounded-full p-1.5 mr-2">
                <DocumentTextIcon className="h-4 w-4 text-white" />
              </div>
              <h3 className="text-base font-semibold text-indigo-900 dark:text-indigo-300">
                {t('projects.fields.documents') || 'Dokumentumok'}
              </h3>
            </div>
            
            <div className="space-y-2">
              {project.documents.map((doc, index) => (
                <div
                  key={doc.id || index}
                  className="flex items-center justify-between p-2 bg-white dark:bg-gray-700 rounded-lg border border-indigo-100 dark:border-indigo-600 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 transition-colors"
                >
                  <div className="flex items-center space-x-2">
                    <span className="text-sm">📄</span>
                    <div>
                      <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {doc.originalName || doc.name}
                      </div>
                      <div className="text-xs text-gray-500 dark:text-gray-400">
                        {doc.size ? `${Math.round(doc.size / 1024)} KB` : '-'} • {
                          (() => {
                            try {
                              if (!doc.uploadedAt) return 'Ismeretlen dátum';
                              
                              let date: Date;
                              if (doc.uploadedAt instanceof Date) {
                                date = doc.uploadedAt;
                              } else if (typeof doc.uploadedAt === 'object' && (doc.uploadedAt as any).toDate) {
                                // Firestore Timestamp
                                date = (doc.uploadedAt as any).toDate();
                              } else {
                                // String vagy más formátum
                                date = new Date(doc.uploadedAt as any);
                              }
                              
                              if (isNaN(date.getTime())) {
                                return 'Érvénytelen dátum';
                              }
                              
                              return formatDateTime(date);
                            } catch (error) {
                              console.error('Date formatting error:', error, doc.uploadedAt);
                              return 'Dátum hiba';
                            }
                          })()
                        }
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center space-x-1">
                    <button
                      onClick={() => window.open(doc.url, '_blank')}
                      className="p-1.5 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
                      title="Megtekintés"
                    >
                      <EyeIcon className="h-3 w-3" />
                    </button>
                    <a
                      href={doc.url}
                      download={doc.originalName || doc.name}
                      className="p-1.5 text-green-600 dark:text-green-400 hover:text-green-800 dark:hover:text-green-300 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 transition-colors"
                      title="Letöltés"
                    >
                      <DocumentTextIcon className="h-3 w-3" />
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Kapcsolódó feladatok */}
        <div className="bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-800/20 rounded-lg p-4 border border-teal-200 dark:border-teal-700">
          <div className="flex items-center mb-3">
            <div className="bg-teal-500 rounded-full p-1.5 mr-2">
              <ListBulletIcon className="h-4 w-4 text-white" />
            </div>
            <h3 className="text-base font-semibold text-teal-900 dark:text-teal-300">
              {t('projects.sections.tasks') || 'Kapcsolódó feladatok'}
            </h3>
            <span className="ml-2 text-sm text-teal-700 dark:text-teal-300 bg-teal-100 dark:bg-teal-900/20 px-2 py-1 rounded-full">
              {projectTasks.length} {t('tasks.title') || 'feladat'}
            </span>
          </div>
          
          {projectTasks.length > 0 ? (
            <div className="space-y-2">
              {projectTasks.map((task, index) => {
                const getTaskStatusColor = (status: string) => {
                  const isDark = document.documentElement.classList.contains('dark');
                  switch (status) {
                    case 'todo':
                      return isDark ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-800 border-gray-300';
                    case 'in_progress':
                      return isDark ? 'bg-blue-900/20 text-blue-300 border-blue-600' : 'bg-blue-100 text-blue-800 border-blue-300';
                    case 'completed':
                      return isDark ? 'bg-green-900/20 text-green-300 border-green-600' : 'bg-green-100 text-green-800 border-green-300';
                    case 'cancelled':
                      return isDark ? 'bg-red-900/20 text-red-300 border-red-600' : 'bg-red-100 text-red-800 border-red-300';
                    default:
                      return isDark ? 'bg-gray-700 text-gray-300 border-gray-600' : 'bg-gray-100 text-gray-800 border-gray-300';
                  }
                };

                const getTaskPriorityColor = (priority: string) => {
                  const isDark = document.documentElement.classList.contains('dark');
                  switch (priority) {
                    case 'low':
                      return isDark ? 'bg-green-900/20 text-green-300' : 'bg-green-100 text-green-800';
                    case 'medium':
                      return isDark ? 'bg-yellow-900/20 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
                    case 'high':
                      return isDark ? 'bg-orange-900/20 text-orange-300' : 'bg-orange-100 text-orange-800';
                    case 'urgent':
                      return isDark ? 'bg-red-900/20 text-red-300' : 'bg-red-100 text-red-800';
                    default:
                      return isDark ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
                  }
                };

                return (
                  <div
                    key={task.id || index}
                    className="flex items-center justify-between p-3 bg-white dark:bg-gray-700 rounded-lg border border-teal-100 dark:border-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 transition-colors"
                  >
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="flex items-center space-x-2">
                        <BriefcaseIcon className="h-4 w-4 text-teal-600 dark:text-teal-400" />
                        <span className="text-xs font-mono text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-gray-600 px-1.5 py-0.5 rounded">
                          {task.taskNumber || 'N/A'}
                        </span>
                      </div>
                      
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center space-x-2">
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                            {task.title}
                          </div>
                          <div className="flex items-center space-x-1">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium border ${getTaskStatusColor(task.status)}`}>
                              {t(`tasks.status.${task.status}`) || task.status}
                            </span>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${getTaskPriorityColor(task.priority)}`}>
                              {t(`tasks.priority.${task.priority}`) || task.priority}
                            </span>
                          </div>
                        </div>
                        
                        <div className="flex items-center space-x-3 mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {task.assignedToName && (
                            <div className="flex items-center">
                              <UserIcon className="h-3 w-3 mr-1" />
                              <span>{task.assignedToName}</span>
                            </div>
                          )}
                          {task.dueDate && (
                            <div className="flex items-center">
                              <CalendarIcon className="h-3 w-3 mr-1" />
                              <span>{formatDateTime(task.dueDate)}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="text-center py-6 text-gray-500 dark:text-gray-400">
              <ListBulletIcon className="h-12 w-12 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
              <p className="text-sm">{t('projects.messages.noTasks') || 'Nincs kapcsolódó feladat'}</p>
            </div>
          )}
        </div>

        {/* Metaadatok */}
        <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs text-gray-600 dark:text-gray-400">
            <div>
              <label className="font-medium">
                {t('common.createdAt') || 'Létrehozva'}:
              </label>
              <div>{formatDateTime(project.createdAt)}</div>
            </div>
            <div>
              <label className="font-medium">
                {t('common.updatedAt') || 'Frissítve'}:
              </label>
              <div>{formatDateTime(project.updatedAt)}</div>
            </div>
            <div>
              <label className="font-medium">
                {t('common.createdBy') || 'Létrehozó'}:
              </label>
              <div>{project.createdByName || '-'}</div>
            </div>
          </div>
        </div>

        </div>
      </Modal>
    </>
  );
};

export default ProjectView; 